import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../ui/sheet';
import {
  FUNDING_RIGHT_DRAWER_CLASS,
} from './fundingDrawerUi';
import { formatCurrency, formatDate, formatTimeDuration } from '../../../utils/fundingWorkspaceFormatters';
import BureauPullSelector, {
  bureauPullScoresFromApplication,
  inferSelectedBureauPull,
} from './BureauPullSelector';
import FundingCopyableField from './FundingCopyableField';
import CreateApplicationDrawer from './CreateApplicationDrawer';
import SecureEinCell from './SecureEinCell';
import SecureSsnField from './SecureSsnField';
import SecureApplicationPasswordField from './SecureApplicationPasswordField';
import { formatApplicationSource, formatBureauPullLabel, formatApplicantPills } from './fundingApplicationDisplay';
import { resolveClientIntakeDisplay } from '../../../utils/fundingClientIntakeDisplay';
import { resolveSelectedClientId } from '../../../services/funding/fundingClientsService';

const Section = ({ title, children }) => (
  <div className="space-y-3 border-b border-[var(--fg-line)] pb-5 last:border-0">
    <h4 className="text-sm font-semibold text-[var(--fg-text)]">{title}</h4>
    {children}
  </div>
);

const ViewField = ({ label, children }) => (
  <div>
    <div className="text-xs text-[var(--fg-muted)]">{label}</div>
    <div className="mt-0.5 text-sm">{children}</div>
  </div>
);

const ApplicationRecordView = ({
  open,
  onOpenChange,
  application,
  client,
  clientId,
  agencyId,
  onDuplicate,
  onRequestEdit,
}) => {
  const [draft, setDraft] = useState(application || {});
  const selectedBureauPull = inferSelectedBureauPull(application);
  const bureauScores = bureauPullScoresFromApplication(application);
  const clientData = useMemo(() => resolveClientIntakeDisplay(client || {}), [client]);
  const resolvedClientId = clientId || resolveSelectedClientId(client);

  useEffect(() => {
    if (open && application) setDraft(application);
  }, [open, application]);

  if (!application) return null;

  const maskDob = (value) => {
    if (!value) return '';
    const yearMatch = String(value).match(/(\d{4})/);
    return yearMatch ? `**/**/${yearMatch[1]}` : '••/••/••••';
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        className={`${FUNDING_RIGHT_DRAWER_CLASS} w-full overflow-y-auto sm:max-w-xl`}
        data-testid="application-record-drawer"
      >
        <SheetHeader>
          <SheetTitle>Application Record</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-5 px-1">
          <Section title="Entity & Applicants">
            <div className="grid gap-3 sm:grid-cols-2">
              <ViewField label="Entity">
                <span className="fg-entity-pill">{application.entityLabel || application.businessLegalName || '—'}</span>
              </ViewField>
              <ViewField label="Applicants">
                <div className="fg-applicant-pills">{formatApplicantPills(application)}</div>
              </ViewField>
              <ViewField label="Applicant Name">
                <FundingCopyableField inline value={draft.ownerName} testId="record-applicant-name" />
              </ViewField>
              <ViewField label="Ownership %">
                <FundingCopyableField inline value={draft.ownershipPercentUsed != null ? `${draft.ownershipPercentUsed}%` : draft.ownershipPercentage != null ? `${draft.ownershipPercentage}%` : ''} testId="record-ownership" />
              </ViewField>
              <ViewField label="Source">{formatApplicationSource(application.source)}</ViewField>
            </div>
          </Section>

          <Section title="Product & Lender">
            <div className="grid gap-3 sm:grid-cols-2">
              <ViewField label="Date of Application">
                <FundingCopyableField inline value={draft.dateOfApplication} displayValue={formatDate(draft.dateOfApplication)} testId="record-date" />
              </ViewField>
              <ViewField label="Lender"><FundingCopyableField inline value={draft.lender} testId="record-lender" /></ViewField>
              <ViewField label="Lending Product"><FundingCopyableField inline value={draft.lendingProduct} testId="record-product" /></ViewField>
              <ViewField label="Type"><FundingCopyableField inline value={draft.applicationType} testId="record-type" /></ViewField>
              <ViewField label="Pers/Biz"><FundingCopyableField inline value={draft.personalOrBusiness} testId="record-pers-biz" /></ViewField>
            </div>
          </Section>

          <Section title="Decision">
            <div className="grid gap-3 sm:grid-cols-2">
              <ViewField label="Decision"><FundingCopyableField inline value={draft.decision} testId="record-decision" /></ViewField>
              <ViewField label="Approval Amount">
                <FundingCopyableField inline value={draft.approvalAmount} displayValue={formatCurrency(draft.approvalAmount)} testId="record-approval" />
              </ViewField>
              <ViewField label="0% Offer"><FundingCopyableField inline value={draft.zeroPercentOffer} testId="record-zero-offer" /></ViewField>
              <ViewField label="0% Term"><FundingCopyableField inline value={draft.zeroPercentTerm} testId="record-zero-term" /></ViewField>
            </div>
          </Section>

          <Section title="Bureau Pull">
            <p className="text-sm text-[var(--fg-muted)]">{formatBureauPullLabel(selectedBureauPull)}</p>
            <div className="mt-3">
              <BureauPullSelector
                bureauPullScores={bureauScores}
                selectedBureauPull={selectedBureauPull}
                readOnly
              />
            </div>
          </Section>

          <Section title="Contact">
            <div className="grid gap-3 sm:grid-cols-2">
              <ViewField label="Email">
                <FundingCopyableField inline value={draft.email || clientData.email} testId="record-email" />
              </ViewField>
              <ViewField label="Phone">
                <FundingCopyableField inline value={draft.phone || clientData.phone} testId="record-phone" />
              </ViewField>
            </div>
          </Section>

          <Section title="Identity & Business">
            <div className="grid gap-3 sm:grid-cols-2">
              <ViewField label="Owner Name"><FundingCopyableField inline value={draft.ownerName} testId="record-owner-name" /></ViewField>
              <ViewField label="SSN">
                <SecureSsnField
                  maskedValue={application?.ssnMasked}
                  ssnLast4={application?.ssnLast4}
                  clientId={resolvedClientId}
                  agencyId={agencyId}
                  applicationId={application?.id}
                  testId="record-ssn"
                />
              </ViewField>
              <ViewField label="DOB">
                <FundingCopyableField
                  inline
                  value={draft.dob}
                  displayValue={formatDate(draft.dob)}
                  maskedValue={maskDob(draft.dob)}
                  isSensitive
                  canReveal={Boolean(draft.dob)}
                  onReveal={async () => draft.dob || null}
                  copyValue={draft.dob}
                  copyRequiresReveal
                  testId="record-dob"
                />
              </ViewField>
              <ViewField label="Full EIN"><SecureEinCell value={draft.fullEin || draft.einMasked} testId="record-ein" /></ViewField>
              <ViewField label="Business Legal Name">
                <FundingCopyableField inline value={draft.businessLegalName || clientData.businessLegalName} testId="record-business-name" />
              </ViewField>
              <ViewField label="Business Start Date">
                <FundingCopyableField inline value={draft.businessStartDate} displayValue={formatDate(draft.businessStartDate)} testId="record-business-start" />
              </ViewField>
              <ViewField label="Employer"><FundingCopyableField inline value={draft.employerName} testId="record-employer" /></ViewField>
              <ViewField label="Time Live in House">
                <FundingCopyableField inline value={formatTimeDuration(draft.timeLiveInHouseYears, draft.timeLiveInHouseMonths)} testId="record-time-live" />
              </ViewField>
              <ViewField label="Time Working At">
                <FundingCopyableField inline value={formatTimeDuration(draft.timeWorkingAtYears, draft.timeWorkingAtMonths)} testId="record-time-working" />
              </ViewField>
              <ViewField label="Personal Address"><FundingCopyableField inline value={draft.personalAddress} testId="record-personal-address" /></ViewField>
              <ViewField label="Business Address"><FundingCopyableField inline value={draft.businessAddress} testId="record-business-address" /></ViewField>
            </div>
          </Section>

          <Section title="Application Access">
            <div className="grid gap-3 sm:grid-cols-2">
              <ViewField label="Application Email Created">
                <FundingCopyableField inline value={clientData.applicationEmailCreated} testId="record-application-email" />
              </ViewField>
              <ViewField label="Application Password Created">
                <SecureApplicationPasswordField
                  hasPassword={clientData.hasApplicationPasswordCreated}
                  maskedValue={clientData.applicationPasswordCreatedMasked}
                  clientId={resolvedClientId}
                  agencyId={agencyId}
                  testId="record-application-password"
                />
              </ViewField>
            </div>
          </Section>

          <Section title="Income & Assets">
            <div className="grid gap-3 sm:grid-cols-2">
              <ViewField label="Personal Income">
                <FundingCopyableField inline value={draft.personalIncome} displayValue={formatCurrency(draft.personalIncome)} testId="record-personal-income" />
              </ViewField>
              <ViewField label="Business Income">
                <FundingCopyableField inline value={draft.businessIncome} displayValue={formatCurrency(draft.businessIncome)} testId="record-business-income" />
              </ViewField>
              <ViewField label="Housing">
                <FundingCopyableField inline value={draft.housing} displayValue={formatCurrency(draft.housing)} testId="record-housing" />
              </ViewField>
              <ViewField label="Business Expenses">
                <FundingCopyableField inline value={draft.businessExpenses} displayValue={formatCurrency(draft.businessExpenses)} testId="record-business-expenses" />
              </ViewField>
              <ViewField label="Savings">
                <FundingCopyableField inline value={draft.savings} displayValue={formatCurrency(draft.savings)} testId="record-savings" />
              </ViewField>
              <ViewField label="Retirement / Stocks">
                <FundingCopyableField inline value={draft.retirementAccount} displayValue={formatCurrency(draft.retirementAccount)} testId="record-retirement" />
              </ViewField>
            </div>
          </Section>

          <Section title="Notes">
            <FundingCopyableField inline value={draft.notes} testId="record-notes" />
          </Section>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => onDuplicate?.(application)}>
              Duplicate
            </Button>
            <Button
              className="flex-1 rounded-xl bg-[var(--fg-accent)]"
              data-testid="edit-application-record-btn"
              onClick={() => onRequestEdit?.(application)}
            >
              Edit Record
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const FundingApplicationRecordDrawer = ({
  open,
  onOpenChange,
  application,
  client,
  clientId,
  agencyId,
  startInEdit = false,
  onSave,
  onDuplicate,
  onRequestEdit,
  saving = false,
  onOpenIdentity,
}) => {
  if (open && application && !startInEdit) {
    return (
      <ApplicationRecordView
        open={open}
        onOpenChange={onOpenChange}
        application={application}
        client={client}
        clientId={clientId}
        agencyId={agencyId}
        onDuplicate={onDuplicate}
        onRequestEdit={onRequestEdit}
      />
    );
  }

  return (
    <CreateApplicationDrawer
      open={open}
      onOpenChange={onOpenChange}
      client={client}
      application={application}
      clientId={clientId}
      agencyId={agencyId}
      onSave={onSave}
      saving={saving}
      onOpenIdentity={onOpenIdentity}
    />
  );
};

export default FundingApplicationRecordDrawer;
